import * as localforage from 'localforage';
import * as PropTypes from 'prop-types';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withContext,
  withState,
} from 'recompose';

// presentation components
import LoadingAnimation from '../../components/LoadingAnimation';
import env from '../../env';

const sessionValidationTime = 1200000; // 20minutes
// const sessionValidationTime = 60000; // DEV: 1 minute

export interface PrivateContainerProps {
    session?: any,
    setSession: (session: any) => void
}

const isLoading = (props: PrivateContainerProps): boolean => (typeof props.session === 'undefined');

const getPHPCookie = (cookie: string) => cookie.match('PHPSESSID');
const getSessionId = (): string => {
    const cookies   = window.document.cookie.split(/\s*;\s*/);
    const phpCookie = cookies.length && cookies.filter(getPHPCookie);

    if (phpCookie.length) {
        return phpCookie[0].split('=')[1];
    }

    return null;
};

const getJWTFetchConfig = (token: string): RequestInit => ({
    method: 'GET',
    mode: 'cors',
    headers: new Headers({
        Authorization: `Bearer ${token}`,
    }),
    cache: 'no-cache'
});

const getSessionToken = () => localforage.getItem<string>('token');

const getAuthFetchConfig = async (defaultCredentials = 'same-origin'): Promise<RequestInit> => {
    const crmCookieSession = getSessionId();

    const config: any = {
        method: 'GET',
        credentials: defaultCredentials,
        cache: 'no-cache',
        headers: new Headers({
            'Content-Type': 'application/json'
        })
    };

    if (crmCookieSession) {
        config.headers.append('x-session-id', crmCookieSession);
    }

    let token: string|null = await localforage.getItem<string>('token');

    if (env.CONVERSATION_UI_DEV && !token) {
        do {
            token = prompt('Token:');
        } while (token === null || token === '');
    }

    config.mode = 'cors';
    config.credentials = 'include';

    if (token) {
        config.headers.append('Authorization', `Bearer ${token}`);
    }

    return config;
};

const clearOfflineData = () => {
    localforage.removeItem('token');
    localforage.removeItem('dealer');
};

const checkAuth = async (setSession, config?: RequestInit) => {
    const checkEndPoint = `${env.WEBAPP_HOST}v2/api/auth/me?info[]=dealer`;
    const fetchConfig = config || await getAuthFetchConfig('include');
    let request: void | Response;
    let payload = null;

    if (fetchConfig) {
        request = await fetch(checkEndPoint, fetchConfig);

        if (request && !request.ok) {
            clearOfflineData();
            setSession(false);
            checkAuth(setSession);
            return;
        }

        if (request) {
            payload = await request.json();
        }

        if (!payload || !payload.token) {
            clearOfflineData();
            setSession(false);
            checkAuth(setSession);
            return;
        }

        await localforage.setItem<string>('token', payload.token);
        await localforage.setItem<object>('dealer', payload.info.dealer);

        return setSession({
            ...payload.user,
            token: payload.token,
            dealer: payload.info.dealer
        });
    }

    setSession(false);
    return;
};

const onValidateSession = async (setSession) => {
    const token = await getSessionToken();
    const JWTFetchConfig = token ? getJWTFetchConfig(token) : null;

    checkAuth(setSession, JWTFetchConfig);
};

export default compose(
    withState('session', 'setSession', undefined),
    withContext({
        session: PropTypes.any,
        setSession: PropTypes.func.isRequired
    }, (props: PrivateContainerProps) => ({
        session: props.session,
        setSession: props.setSession
    })),
    lifecycle<PrivateContainerProps, {}>({
        async componentWillMount() {
            const { setSession } = this.props;

            await checkAuth(setSession);

            // TODO: extend lifecycle wrapper class to better type session Timer property
            (this as any).sessionTimer = setInterval(onValidateSession.bind(null, setSession), sessionValidationTime);
        },
        componentWillUnmount() {
            // TODO: extend lifecycle wrapper class to better type session Timer property
            const sessionTime = (this as any).sessionTime;

            sessionTime && clearInterval(sessionTime);
        }
    }),
    branch(isLoading, renderComponent(LoadingAnimation))
);



// WEBPACK FOOTER //
// ./src/sections/Login/PrivateContainer.ts